import * as Sentry from '@sentry/react'
import type { AccommodationDetail, LocationSearchResult } from '@/api/accommodation/types'

import { pushToDataLayer } from './providers/gtm'
import { trackMixpanel, identifyMixpanelUser } from './providers/mixpanel'
import { offerSource, rememberSimilarUnit } from './shared/similarSource'
import { trackClarityEvent, setClarityTag } from './providers/clarity'
import { hashPII } from './shared/hashPII'
import {
  mapAccommodation,
  mapOffer,
  nightsBetween,
  trackingValue,
  offerContentData,
  CURRENCY,
  type LocationLike,
  type UnitLike,
  type Prices,
  type SearchFilters,
} from './shared/mappers'

// Initialization + types re-exported so consumers only import from '@/lib/track'.
export { initializeGTM } from './providers/gtm'
export { initializeMixpanel } from './providers/mixpanel'
export { initializeClarity } from './providers/clarity'
export { captureUtmParams } from './shared/utmParams'
export type { Prices, SearchFilters } from './shared/mappers'

/**
 * Unified tracking facade. Each function represents one domain event and fans
 * out to the relevant providers (GTM, Mixpanel) with their existing event names
 * and payloads. Clarity is mirrored automatically inside the GTM provider.
 *
 * Call sites import a single function from here instead of wiring each provider
 * by hand.
 */

// =============================================================================
// Search
// =============================================================================

export function trackSearchResultsViewed(
  results: LocationSearchResult[],
  filters?: SearchFilters,
  view?: 'list' | 'map',
): void {
  pushToDataLayer({
    event: 'search_offers_listed',
    offers: results.map(mapOffer),
    count: results.length,
    view,
  })
  trackMixpanel('search_results_viewed', {
    results_count: results.length,
    view,
    filter_date_from: filters?.fromDate,
    filter_date_to: filters?.toDate,
    filter_region: filters?.region,
    filter_label_id: filters?.labelId,
    filter_beds: filters?.beds,
    filter_cribs: filters?.cribs,
    filter_adults_only: filters?.adult,
    filter_pets: filters?.pets,
    filter_facility: filters?.facility,
    filter_category: filters?.category,
    sort_order: filters?.order,
    sort_direction: filters?.direction,
  })
}

export function trackSearchFiltersApplied(filters: SearchFilters): void {
  pushToDataLayer({
    event: 'search_filter_applied',
    filters: {
      dateFrom: filters.fromDate,
      dateTo: filters.toDate,
      region: filters.region,
      labelId: filters.labelId,
      beds: filters.beds,
      cribs: filters.cribs,
      adultsOnly: filters.adult,
      pets: filters.pets,
      facility: filters.facility,
      category: filters.category,
    },
  })
  trackMixpanel('search_filter_applied', {
    filter_date_from: filters.fromDate,
    filter_date_to: filters.toDate,
    filter_region: filters.region,
    filter_label_id: filters.labelId,
    filter_beds: filters.beds,
    filter_cribs: filters.cribs,
    filter_adults_only: filters.adult,
    filter_pets: filters.pets,
    filter_facility: filters.facility,
    filter_category: filters.category,
  })
}

export function trackSearchCategoryFilterApplied(categorySlugs: string[]): void {
  pushToDataLayer({
    event: 'search_category_filter_applied',
    category: {
      slugs: categorySlugs,
      count: categorySlugs.length,
    },
  })
  trackMixpanel('search_category_filter_applied', {
    category_slugs: categorySlugs,
    category_count: categorySlugs.length,
  })
}

export function trackSearchFacilityFilterApplied(facilityKeys: string[]): void {
  pushToDataLayer({
    event: 'search_facility_filter_applied',
    facility: {
      keys: facilityKeys,
      count: facilityKeys.length,
    },
  })
  trackMixpanel('search_facility_filter_applied', {
    facility_keys: facilityKeys,
    facility_count: facilityKeys.length,
  })
}

export function trackSearchLabelFilterSelected(labelId: number | undefined, labelName: string | undefined): void {
  pushToDataLayer({
    event: 'search_label_filter_selected',
    labelId,
    labelName,
  })
  trackMixpanel('search_label_filter_selected', {
    filter_label_id: labelId,
    filter_label_name: labelName,
  })
}

export function trackSearchSortChanged(order: string, direction: string): void {
  pushToDataLayer({ event: 'search_sort_changed', order, direction })
  trackMixpanel('search_sort_changed', {
    sort_order: order,
    sort_direction: direction,
  })
}

export function trackSearchLoadedMore(currentCount: number): void {
  pushToDataLayer({ event: 'search_auto_loaded_more', currentCount })
  trackMixpanel('search_auto_loaded_more', {
    current_results_count: currentCount,
  })
}

export function trackSearchMapOpened(resultsCount: number): void {
  pushToDataLayer({ event: 'search_map_opened', count: resultsCount })
  trackMixpanel('search_map_opened', { results_count: resultsCount })
}

// Pin click and card swipe both surface one offer on the map — same payload.
function trackSearchMapOfferShown(event: string, result: LocationSearchResult): void {
  const prices = {
    dailyDiscountPriceFrom: result.minDiscountPrice,
    dailyStandardPriceFrom: result.standardPrice,
  }
  pushToDataLayer({
    event,
    accommodation: mapAccommodation(result.location, result.unit),
    prices,
    value: trackingValue(prices),
    currency: CURRENCY,
  })
  trackMixpanel(event, {
    accommodation: mapAccommodation(result.location, result.unit),
    price_daily_discount: result.minDiscountPrice,
    price_daily_standard: result.standardPrice,
  })
}

export function trackSearchMapPinClicked(result: LocationSearchResult): void {
  trackSearchMapOfferShown('search_map_pin_clicked', result)
}

export function trackSearchMapCardSwiped(result: LocationSearchResult): void {
  trackSearchMapOfferShown('search_map_card_swiped', result)
}

export function trackSearchViewTimeSpent(view: 'list' | 'map', seconds: number): void {
  pushToDataLayer({ event: 'search_view_time_spent', view, seconds })
  trackMixpanel('search_view_time_spent', {
    view,
    duration_seconds: seconds,
  })
}

export function trackSearchNoResults(filters: SearchFilters, view?: 'list' | 'map'): void {
  pushToDataLayer({
    event: 'search_no_results',
    view,
    filters: {
      dateFrom: filters.fromDate,
      dateTo: filters.toDate,
      region: filters.region,
      labelId: filters.labelId,
      beds: filters.beds,
      cribs: filters.cribs,
      adultsOnly: filters.adult,
      pets: filters.pets,
      facility: filters.facility,
    },
  })
  trackMixpanel('search_no_results', {
    view,
    filter_date_from: filters.fromDate,
    filter_date_to: filters.toDate,
    filter_region: filters.region,
    filter_label_id: filters.labelId,
    filter_beds: filters.beds,
    filter_cribs: filters.cribs,
    filter_adults_only: filters.adult,
    filter_pets: filters.pets,
    filter_facility: filters.facility,
  })
}

export function trackSearchSimilarShown(
  count: number,
  resultsCount: number,
  differences: string[],
  filters: SearchFilters,
): void {
  pushToDataLayer({
    event: 'search_similar_shown',
    count,
    resultsCount,
    differences,
  })
  trackMixpanel('search_similar_shown', {
    similar_count: count,
    results_count: resultsCount,
    similar_differences: differences,
    filter_date_from: filters.fromDate,
    filter_date_to: filters.toDate,
    filter_region: filters.region,
    filter_category: filters.category,
    filter_label_id: filters.labelId,
    filter_facility: filters.facility,
  })
}

export function trackSearchFiltersCleared(): void {
  pushToDataLayer({ event: 'search_filters_cleared' })
  trackMixpanel('search_filters_cleared', {})
}

export function trackOfferClicked(
  result: LocationSearchResult,
  position?: number,
  source?: 'list' | 'map' | 'article' | 'landing' | 'similar',
  /** For a similar offer, the types of criteria it misses (e.g. ['facility', 'date']). */
  differences?: string[],
): void {
  if (source === 'similar') {
    rememberSimilarUnit(result.unit.id)
  }
  const clickPrices = {
    dailyDiscountPriceFrom: result.minDiscountPrice,
    dailyStandardPriceFrom: result.standardPrice,
  }
  pushToDataLayer({
    event: 'accommodation_clicked',
    accommodation: mapAccommodation(result.location, result.unit),
    position,
    source,
    differences,
    dateFrom: result.start,
    dateTo: result.end,
    prices: clickPrices,
    value: trackingValue(clickPrices),
    currency: CURRENCY,
  })
  trackMixpanel('accommodation_clicked', {
    accommodation: mapAccommodation(result.location, result.unit),
    position_in_list: position,
    source,
    similar_differences: differences,
    available_date_from: result.start,
    available_date_to: result.end,
    price_daily_discount: result.minDiscountPrice,
    price_daily_standard: result.standardPrice,
    min_nights: result.minNights,
  })
}

// =============================================================================
// Homepage
// =============================================================================

export function trackHomeExploreClicked(): void {
  pushToDataLayer({ event: 'home_explore_clicked' })
  trackMixpanel('home_explore_clicked', {})
}

export function trackHomeStayTypeClicked(labelId: number | undefined, labelName: string | undefined): void {
  pushToDataLayer({ event: 'home_stay_type_clicked', labelId, labelName })
  trackMixpanel('home_stay_type_clicked', {
    label_id: labelId,
    label_name: labelName,
  })
}

// =============================================================================
// Accommodation detail
// =============================================================================

export function trackAccommodationViewed(location: AccommodationDetail, prices?: Prices): void {
  pushToDataLayer({
    event: 'accommodation_detail',
    accommodation: mapAccommodation(location, location.unit),
    offerSource: offerSource(location.unit.id),
    value: trackingValue(prices),
    currency: CURRENCY,
    ...offerContentData(location),
  })
  trackMixpanel('accommodation_detail_viewed', {
    accommodation: mapAccommodation(location, location.unit),
    offer_source: offerSource(location.unit.id),
    price_daily_discount: prices?.dailyDiscountPriceFrom,
    price_daily_standard: prices?.dailyStandardPriceFrom,
  })
}

export function trackAccommodationTimeSpent(location: AccommodationDetail, seconds: number): void {
  pushToDataLayer({
    event: 'accommodation_detail_time_spent',
    accommodation: mapAccommodation(location, location.unit),
    seconds,
  })
  trackMixpanel('accommodation_detail_time_spent', {
    accommodation: mapAccommodation(location, location.unit),
    duration_seconds: seconds,
  })
}

export function trackReservationCalendarOpened(location: AccommodationDetail, prices?: Prices): void {
  pushToDataLayer({
    event: 'accommodation_reservation_calendar_opened',
    accommodation: mapAccommodation(location, location.unit),
    prices: prices ?? {},
    value: trackingValue(prices),
    currency: CURRENCY,
    ...offerContentData(location),
  })
  trackMixpanel('reservation_calendar_opened', {
    accommodation: mapAccommodation(location, location.unit),
    price_daily_discount: prices?.dailyDiscountPriceFrom,
    price_daily_standard: prices?.dailyStandardPriceFrom,
  })
}

export function trackReservationDatesSelected(
  location: AccommodationDetail,
  prices: Prices,
  dateFrom: Date,
  dateTo: Date,
): void {
  pushToDataLayer({
    event: 'accommodation_reservation_calendar_changed',
    accommodation: mapAccommodation(location, location.unit),
    prices,
    dateFrom,
    dateTo,
    value: trackingValue(prices),
    currency: CURRENCY,
    ...offerContentData(location),
  })
  trackMixpanel('reservation_dates_selected', {
    accommodation: mapAccommodation(location, location.unit),
    date_from: dateFrom,
    date_to: dateTo,
    nights: nightsBetween(dateFrom, dateTo),
    price_total_discount: prices.totalDiscountPrice,
    price_total_standard: prices.totalStandardPrice,
  })
}

export function trackReservationProceeded(
  location: AccommodationDetail,
  prices: Prices,
  dateFrom: Date,
  dateTo: Date,
): void {
  pushToDataLayer({
    event: 'accommodation_reservation_proceed_clicked',
    accommodation: mapAccommodation(location, location.unit),
    prices,
    dateFrom,
    dateTo,
    value: trackingValue(prices),
    currency: CURRENCY,
    ...offerContentData(location),
  })
  setClarityTag('funnel_step', 'reservation_proceed_clicked')
  trackMixpanel('reservation_proceed_clicked', {
    accommodation: mapAccommodation(location, location.unit),
    date_from: dateFrom,
    date_to: dateTo,
    nights: nightsBetween(dateFrom, dateTo),
    price_total_discount: prices.totalDiscountPrice,
    price_total_standard: prices.totalStandardPrice,
  })
}

// =============================================================================
// Reservation / checkout
// =============================================================================

export function trackReservationCheckoutStarted(
  location: AccommodationDetail,
  prices: Prices,
  dateFrom: Date,
  dateTo: Date,
): void {
  pushToDataLayer({
    event: 'accommodation_reservation_checkout_started',
    accommodation: mapAccommodation(location, location.unit),
    prices,
    dateFrom,
    dateTo,
    value: trackingValue(prices),
    currency: CURRENCY,
    ...offerContentData(location),
  })
  setClarityTag('funnel_step', 'reservation_checkout_started')
  trackMixpanel('reservation_checkout_started', {
    accommodation: mapAccommodation(location, location.unit),
    date_from: dateFrom,
    date_to: dateTo,
    nights: nightsBetween(dateFrom, dateTo),
    price_total_discount: prices.totalDiscountPrice,
    price_total_standard: prices.totalStandardPrice,
  })
}

export function trackReservationDatesEditOpened(location: AccommodationDetail, dateFrom: Date, dateTo: Date): void {
  pushToDataLayer({
    event: 'accommodation_reservation_dates_edit_opened',
    accommodation: mapAccommodation(location, location.unit),
    dateFrom,
    dateTo,
    ...offerContentData(location),
  })
  trackMixpanel('reservation_dates_edit_opened', {
    accommodation: mapAccommodation(location, location.unit),
    date_from: dateFrom,
    date_to: dateTo,
    nights: nightsBetween(dateFrom, dateTo),
  })
}

export function trackReservationDatesEditConfirmed(
  location: AccommodationDetail,
  prices: Prices,
  dateFrom: Date,
  dateTo: Date,
  previous: { dateFrom: Date; dateTo: Date },
): void {
  // A confirm without an actual change still tells us the visitor came here to
  // check the dates, so it is tracked and flagged instead of being dropped.
  const datesChanged =
    previous.dateFrom.getTime() !== dateFrom.getTime() || previous.dateTo.getTime() !== dateTo.getTime()

  pushToDataLayer({
    event: 'accommodation_reservation_dates_edit_confirmed',
    accommodation: mapAccommodation(location, location.unit),
    prices,
    dateFrom,
    dateTo,
    previousDateFrom: previous.dateFrom,
    previousDateTo: previous.dateTo,
    datesChanged,
    value: trackingValue(prices),
    currency: CURRENCY,
    ...offerContentData(location),
  })
  trackMixpanel('reservation_dates_edit_confirmed', {
    accommodation: mapAccommodation(location, location.unit),
    date_from: dateFrom,
    date_to: dateTo,
    nights: nightsBetween(dateFrom, dateTo),
    previous_date_from: previous.dateFrom,
    previous_date_to: previous.dateTo,
    previous_nights: nightsBetween(previous.dateFrom, previous.dateTo),
    dates_changed: datesChanged,
    price_total_discount: prices.totalDiscountPrice,
    price_total_standard: prices.totalStandardPrice,
  })
}

export function trackReservationValidationFailed(location: AccommodationDetail, invalidFields: string[]): void {
  pushToDataLayer({
    event: 'accommodation_reservation_validation_failed',
    accommodation: mapAccommodation(location, location.unit),
    invalidFields,
  })
  setClarityTag('reservation_validation_failed', invalidFields)
  setClarityTag('funnel_step', 'reservation_validation_failed')
  trackMixpanel('reservation_validation_failed', {
    accommodation: mapAccommodation(location, location.unit),
    invalid_fields: invalidFields,
  })
}

export function trackReservationGuestCountChanged(location: AccommodationDetail, guestCount: number): void {
  pushToDataLayer({
    event: 'accommodation_reservation_guest_count_changed',
    accommodation: mapAccommodation(location, location.unit),
    guestCount,
  })
  setClarityTag('reservation_guest_count', String(guestCount))
  trackMixpanel('reservation_guest_count_changed', {
    accommodation: mapAccommodation(location, location.unit),
    guest_count: guestCount,
  })
}

export function trackReservationCompanyToggled(isCompany: boolean): void {
  pushToDataLayer({
    event: 'accommodation_reservation_company_toggled',
    isCompany,
  })
  trackMixpanel('reservation_company_toggled', { is_company: isCompany })
}

export async function trackReservationConfirmed(
  formData: {
    firstName: string
    lastName: string
    email: string
    phone: string
  },
  location: AccommodationDetail,
  prices: Prices,
  dateFrom: Date,
  dateTo: Date,
): Promise<void> {
  try {
    const [hashedFirstName, hashedLastName, hashedEmail, hashedPhone] = await Promise.all([
      hashPII(formData.firstName),
      hashPII(formData.lastName),
      hashPII(formData.email),
      hashPII(formData.phone),
    ])

    pushToDataLayer({
      event: 'accommodation_reservation_checkout_confirmed',
      user: {
        first_name_hash: hashedFirstName,
        last_name_hash: hashedLastName,
        email_hash: hashedEmail,
        phone_hash: hashedPhone,
      },
      accommodation: mapAccommodation(location, location.unit),
      offerSource: offerSource(location.unit.id),
      prices,
      dateFrom,
      dateTo,
      value: trackingValue(prices),
      currency: CURRENCY,
      ...offerContentData(location),
    })
    setClarityTag('funnel_step', 'reservation_confirmed')
    trackClarityEvent('reservation_confirmed')

    // Identify user by hashed email for cross-session funnel tracking.
    if (hashedEmail) identifyMixpanelUser(hashedEmail)
    trackMixpanel('reservation_checkout_confirmed', {
      accommodation: mapAccommodation(location, location.unit),
      offer_source: offerSource(location.unit.id),
      date_from: dateFrom,
      date_to: dateTo,
      nights: nightsBetween(dateFrom, dateTo),
      price_total_discount: prices.totalDiscountPrice,
      price_total_standard: prices.totalStandardPrice,
      user_email_hash: hashedEmail,
      user_phone_hash: hashedPhone,
    })
  } catch (error) {
    Sentry.captureException(error, {
      tags: { track_event: 'reservation_confirmed' },
    })
  }
}

// =============================================================================
// Payment
// =============================================================================

export async function trackAccommodationPaid(order: {
  number: string
  firstName: string
  lastName: string
  email: string
  phone: string
  start: Date
  end: Date
  payment?: { total: number; paidAt: string | null } | null
  location?: LocationLike | null
  unit?: UnitLike | null
}): Promise<void> {
  try {
    const [hashedFirstName, hashedLastName, hashedEmail, hashedPhone] = await Promise.all([
      hashPII(order.firstName),
      hashPII(order.lastName),
      hashPII(order.email),
      hashPII(order.phone),
    ])

    const paidPrices: Prices = {
      dailyDiscountPriceFrom: undefined,
      dailyStandardPriceFrom: undefined,
      totalDiscountPrice: order.payment?.total,
      totalStandardPrice: undefined,
    }
    pushToDataLayer({
      event: 'accommodation_paid',
      orderNumber: order.number,
      user: {
        first_name_hash: hashedFirstName,
        last_name_hash: hashedLastName,
        email_hash: hashedEmail,
        phone_hash: hashedPhone,
      },
      accommodation: order.location ? mapAccommodation(order.location, order.unit ?? undefined) : undefined,
      offerSource: offerSource(order.unit?.id),
      prices: paidPrices,
      dateFrom: order.start,
      dateTo: order.end,
      value: trackingValue(paidPrices),
      currency: CURRENCY,
    })

    trackMixpanel('accommodation_paid', {
      order_number: order.number,
      offer_source: offerSource(order.unit?.id),
      accommodation: order.location ? mapAccommodation(order.location, order.unit ?? undefined) : undefined,
      date_from: order.start,
      date_to: order.end,
      nights: nightsBetween(order.start, order.end),
      price_total: order.payment?.total,
      user_email_hash: hashedEmail,
    })
  } catch (error) {
    Sentry.captureException(error, {
      tags: { track_event: 'accommodation_paid' },
    })
  }
}

/**
 * A location confirming each booking: the customer's money is held and the
 * request waits for the owner. The purchase itself (`accommodation_paid`, and
 * the server-side conversions) only follows once the owner accepts.
 */
export function trackAccommodationRequestSent(order: {
  number: string
  start: Date
  end: Date
  payment?: { total: number; paidAt: string | null } | null
  location?: LocationLike | null
  unit?: UnitLike | null
}): void {
  const accommodation = order.location ? mapAccommodation(order.location, order.unit ?? undefined) : undefined

  pushToDataLayer({
    event: 'accommodation_request_sent',
    orderNumber: order.number,
    accommodation,
    offerSource: offerSource(order.unit?.id),
    prices: { totalDiscountPrice: order.payment?.total },
    dateFrom: order.start,
    dateTo: order.end,
  })

  trackMixpanel('accommodation_request_sent', {
    order_number: order.number,
    offer_source: offerSource(order.unit?.id),
    accommodation,
    date_from: order.start,
    date_to: order.end,
    nights: nightsBetween(order.start, order.end),
    price_total: order.payment?.total,
  })
}

export function trackOrderPaymentInitiated(orderNumber: string): void {
  pushToDataLayer({ event: 'order_payment_initiated', orderNumber })
  trackMixpanel('order_payment_initiated', { order_number: orderNumber })
}

// =============================================================================
// Inquiry
// =============================================================================

export function trackInquiryOpened(location: AccommodationDetail): void {
  pushToDataLayer({
    event: 'accommodation_location_inquiry_opened',
    accommodation: mapAccommodation(location, location.unit),
  })
  trackMixpanel('inquiry_opened', {
    accommodation: mapAccommodation(location, location.unit),
  })
}

export function trackInquirySubmitted(location: AccommodationDetail): void {
  pushToDataLayer({
    event: 'accommodation_location_inquiry_submitted',
    accommodation: mapAccommodation(location, location.unit),
  })
  trackMixpanel('inquiry_submitted', {
    accommodation: mapAccommodation(location, location.unit),
  })
}

// =============================================================================
// Price guarantee
// =============================================================================

export type PriceGuaranteeComparison = {
  ourPrice: number
  directPrice: number
  sitePrices: Record<string, number>
  isCheapest: boolean
}

export function trackPriceGuaranteeOpened(location: AccommodationDetail, comparison: PriceGuaranteeComparison): void {
  pushToDataLayer({
    event: 'accommodation_price_guarantee_opened',
    accommodation: mapAccommodation(location, location.unit),
    ourPrice: comparison.ourPrice,
    directPrice: comparison.directPrice,
    sitePrices: comparison.sitePrices,
    isCheapest: comparison.isCheapest,
  })
  trackMixpanel('price_guarantee_opened', {
    accommodation: mapAccommodation(location, location.unit),
    our_price: comparison.ourPrice,
    direct_price: comparison.directPrice,
    site_prices: comparison.sitePrices,
    sites_count: Object.keys(comparison.sitePrices).length,
    is_cheapest: comparison.isCheapest,
  })
}

export function trackPriceGuaranteeFormOpened(location: AccommodationDetail): void {
  pushToDataLayer({
    event: 'accommodation_price_guarantee_form_opened',
    accommodation: mapAccommodation(location, location.unit),
  })
  trackMixpanel('price_guarantee_form_opened', {
    accommodation: mapAccommodation(location, location.unit),
  })
}

export function trackPriceGuaranteeSubmitted(
  location: AccommodationDetail,
  report: { url: string; pricePerNight: number },
): void {
  pushToDataLayer({
    event: 'accommodation_price_guarantee_submitted',
    accommodation: mapAccommodation(location, location.unit),
    reportedUrl: report.url,
    reportedPricePerNight: report.pricePerNight,
  })
  trackMixpanel('price_guarantee_submitted', {
    accommodation: mapAccommodation(location, location.unit),
    reported_url: report.url,
    reported_price_per_night: report.pricePerNight,
  })
}

// =============================================================================
// Inspiration / blog
// =============================================================================

export function trackInspirationListViewed(articleCount: number): void {
  pushToDataLayer({
    event: 'inspiration_list_viewed',
    count: articleCount,
  })
  trackMixpanel('inspiration_list_viewed', {
    articles_count: articleCount,
  })
}

export function trackArticleClicked(article: { slug: string; title: string }, position?: number): void {
  pushToDataLayer({
    event: 'article_clicked',
    article: { slug: article.slug, title: article.title },
    position,
  })
  trackMixpanel('article_clicked', {
    article_slug: article.slug,
    article_title: article.title,
    position_in_list: position,
  })
}

export function trackArticleViewed(article: { slug: string; title: string }): void {
  pushToDataLayer({
    event: 'article_viewed',
    article: { slug: article.slug, title: article.title },
  })
  trackMixpanel('article_viewed', {
    article_slug: article.slug,
    article_title: article.title,
  })
}

// =============================================================================
// SEO landing pages
// =============================================================================

export interface LandingPageInfo {
  path: string // canonical URL, e.g. /pobyty/sumava/se-saunou
  kind: string // hub | region | facility | category | combo
  region?: string
  base?: string // facility/category slug
}

export function trackLandingViewed(page: LandingPageInfo): void {
  pushToDataLayer({ event: 'landing_viewed', landing: page })
  trackMixpanel('landing_viewed', {
    landing_path: page.path,
    landing_kind: page.kind,
    landing_region: page.region,
    landing_base: page.base,
  })
}

export function trackLandingRefineClicked(page: LandingPageInfo, target: { region: string; base: string }): void {
  pushToDataLayer({ event: 'landing_refine_clicked', landing: page, target })
  trackMixpanel('landing_refine_clicked', {
    landing_path: page.path,
    landing_kind: page.kind,
    target_region: target.region,
    target_base: target.base,
  })
}

// =============================================================================
// Other
// =============================================================================

export function trackVirtualPageview(pagePath: string, pageTitle: string): void {
  pushToDataLayer({
    event: 'virtual_pageview',
    page_path: pagePath,
    page_title: pageTitle,
  })
  trackMixpanel('page_viewed', {
    page_path: pagePath,
    page_title: pageTitle,
  })
}

export async function trackContactFormSubmitted(formData: {
  firstName: string
  lastName: string
  email: string
  phone?: string
}): Promise<void> {
  try {
    const [hashedFirstName, hashedLastName, hashedEmail, hashedPhone] = await Promise.all([
      hashPII(formData.firstName),
      hashPII(formData.lastName),
      hashPII(formData.email),
      hashPII(formData.phone ?? ''),
    ])

    pushToDataLayer({
      event: 'form_contact',
      user: {
        first_name_hash: hashedFirstName,
        last_name_hash: hashedLastName,
        email_hash: hashedEmail,
        phone_hash: hashedPhone || undefined,
      },
    })
    trackMixpanel('form_contact', {})
  } catch (error) {
    Sentry.captureException(error, {
      tags: { track_event: 'form_contact' },
    })
  }
}

export function trackNewsletterShown(): void {
  pushToDataLayer({ event: 'newsletter_popup_shown' })
  trackMixpanel('newsletter_popup_shown', {})
}

export async function trackNewsletterSubscribed(email: string): Promise<void> {
  try {
    const hashedEmail = await hashPII(email)
    pushToDataLayer({
      event: 'newsletter_subscribe',
      user: { email_hash: hashedEmail },
    })
    trackMixpanel('newsletter_subscribe', {})
  } catch (error) {
    Sentry.captureException(error, {
      tags: { track_event: 'newsletter_subscribe' },
    })
  }
}

// =============================================================================
// Location watchman
// =============================================================================

export function trackLocationWatchmanOpened(location: AccommodationDetail, source: string): void {
  pushToDataLayer({
    event: 'accommodation_watch_dates_opened',
    accommodation: mapAccommodation(location, location.unit),
    source,
  })
  trackMixpanel('watch_dates_opened', {
    accommodation: mapAccommodation(location, location.unit),
    source,
  })
}

export async function trackLocationWatchmanSubscribed(
  slug: string,
  unitId: number,
  email: string,
  source: string,
): Promise<void> {
  try {
    const hashedEmail = await hashPII(email)
    pushToDataLayer({
      event: 'accommodation_watch_dates_subscribed',
      accommodationSlug: slug,
      unitId,
      source,
      user: { email_hash: hashedEmail },
    })
    trackMixpanel('watch_dates_subscribed', {
      accommodation_slug: slug,
      unit_id: unitId,
      source,
    })
  } catch (error) {
    Sentry.captureException(error, {
      tags: { track_event: 'watch_dates_subscribed' },
    })
  }
}

export function trackLocationWatchmanUnsubscribed(slug: string, unitId: number, source: string): void {
  pushToDataLayer({
    event: 'accommodation_watch_dates_unsubscribed',
    accommodationSlug: slug,
    unitId,
    source,
  })
  trackMixpanel('watch_dates_unsubscribed', {
    accommodation_slug: slug,
    unit_id: unitId,
    source,
  })
}

/** The watch heart was clicked without a known e-mail, so it asks for one. */
export function trackWatchHeartEmailPromptOpened(slug: string, unitId: number, source: string): void {
  pushToDataLayer({
    event: 'accommodation_watch_heart_email_prompt_opened',
    accommodationSlug: slug,
    unitId,
    source,
  })
  trackMixpanel('watch_heart_email_prompt_opened', {
    accommodation_slug: slug,
    unit_id: unitId,
    source,
  })
}
